import { Icon } from "@iconify/react";
import { ReactNode } from "react";
import { cn } from "./Card";

export type CollapsibleInfoCardProps = {
  /** Fully-styled title node (caller controls text size/weight/color, badges, etc). */
  title: ReactNode;
  expanded: boolean;
  onToggle: () => void;
  /** Shown under the title only while collapsed, e.g. a one-line summary of the body. */
  collapsedSummary?: ReactNode;
  /** Adds a border between the header and body instead of relying on spacing alone. */
  divider?: boolean;
  className?: string;
  headerClassName?: string;
  bodyClassName?: string;
  children?: ReactNode;
};

export function CollapsibleInfoCard({
  title,
  expanded,
  onToggle,
  collapsedSummary,
  divider = false,
  className,
  headerClassName,
  bodyClassName,
  children,
}: CollapsibleInfoCardProps) {
  return (
    <div
      className={cn(
        "border border-borderColor rounded-lg shadow-box bg-white w-full",
        className
      )}
    >
      <div
        className={cn(
          "flex justify-between p-4",
          collapsedSummary ? "items-start" : "items-center",
          divider && "border-b border-borderColor",
          headerClassName
        )}
      >
        <div className="flex-1 min-w-0">
          {title}
          {!expanded && collapsedSummary && (
            <div className="flex items-center gap-1.5 flex-wrap mt-1">
              {collapsedSummary}
            </div>
          )}
        </div>
        <button
          type="button"
          className="text-gray-light hover:text-primaryText ml-2 flex-shrink-0"
          onClick={onToggle}
        >
          <Icon
            icon={expanded ? "ri:arrow-up-s-line" : "ri:arrow-down-s-line"}
            height={20}
          />
        </button>
      </div>

      {expanded && children != null && (
        <div className={cn("px-4 pb-4", divider && "pt-4", bodyClassName)}>
          {children}
        </div>
      )}
    </div>
  );
}
